import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import UpdateBanner from '@/components/UpdateBanner';
import LandingPage from '@/pages/LandingPage';
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import CommunityPage from '@/pages/CommunityPage';
import EventsPage from '@/pages/EventsPage';
import EventDetailPage from '@/pages/EventDetailPage';
import DirectoryPage from '@/pages/DirectoryPage';
import ChaptersPage from '@/pages/ChaptersPage';
import ChapterDetailPage from '@/pages/ChapterDetailPage';
import MessagesPage from '@/pages/MessagesPage';
import GroupChatsPage from '@/pages/GroupChatsPage';
import MarketplacePage from '@/pages/MarketplacePage';
import ProfilePage from '@/pages/ProfilePage';
import AdminDashboard from '@/pages/AdminDashboard';
import AreaManagerDashboard from '@/pages/ChapterLeaderDashboard';
import ReportsPage from '@/pages/ReportsPage';
import InstallPage from '@/pages/InstallPage';
import Layout from '@/components/Layout';
import axios from 'axios';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={!user ? <LandingPage /> : <Navigate to="/home" />} />
          <Route path="/login" element={!user ? <LoginPage setUser={setUser} /> : <Navigate to="/home" />} />
          <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/home" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/home" />} />
          <Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/home" />} />
          <Route path="/install" element={<InstallPage />} />
          
          <Route element={<Layout user={user} logout={logout} />}>
            <Route path="/home" element={
              user ? <HomePage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/community" element={
              user ? <CommunityPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/events" element={
              user ? <EventsPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/events/:eventId" element={
              user ? <EventDetailPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/directory" element={
              user ? <DirectoryPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/chapters" element={
              user ? <ChaptersPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/chapters/:chapterName" element={
              user ? <ChapterDetailPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/messages" element={
              user ? <MessagesPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/messages/:userId" element={
              user ? <MessagesPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/group-chats" element={
              user ? <GroupChatsPage user={user} /> : <Navigate to="/login" />
            } />
            <Route path="/marketplace" element={
              user ? <MarketplacePage user={user} /> : <Navigate to="/login" />
            } />
            {/* Profile page always accessible */}
            <Route path="/profile" element={user ? <ProfilePage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
            {user?.is_admin && <Route path="/admin" element={<AdminDashboard />} />}
            {(user?.is_area_manager || user?.is_admin) && <Route path="/area-manager" element={<AreaManagerDashboard user={user} />} />}
            {(user?.is_area_manager || user?.is_admin) && <Route path="/reports" element={<ReportsPage user={user} />} />}
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
      <UpdateBanner />
    </div>
  );
}

export default App;
